<script lang="ts">
  import type { SpacingDensities } from '../../../types/misc';
  import { SpacingDensity } from '../../../types/misc';
  import { MODULE_INJECTION } from '../Module/Module.keys';
  import type { Layouts, TableRadiuses } from './Table.types';

  export * from './Table.keys';
  export * from './Table.types';

  export interface TableProps {
    /**
     * Controls the Table's padding; the default value is "comfortable". On small screens, "compact" density is applied regardless of this prop's value.
     */
    density?: SpacingDensities;

    /**
     * Sets the text for the empty state; the default value is "No Results".
     */
    emptyStateText?: string;

    /**
     * Styles the last column for "row actions"
     */
    hasActions?: boolean;

    /**
     * If true, hides the default expansion toggle column
     */
    hasCustomExpandToggle?: boolean;

    /**
     * Shows the empty state
     */
    isEmpty?: boolean;

    /**
     * When true, indicates the table is being composed by DataTable (schema-driven mode).
     * TableRow/TableHeaderRow use this to suppress their built-in selection cells.
     */
    isSchemaDriven?: boolean;

    /**
     * Shows the loading state
     */
    isLoading?: boolean;

    /**
     * Adds a toggle column for row expansion. This is primarily needed for ensuring the corresponding empty TableHeaderRow is included.
     */
    isExpandable?: boolean;

    /**
     * Adds a checkbox column for selecting rows; intended for use with the `useSelection` composable.
     */
    isSelectable?: boolean;

    /**
     * Sets the table layout; the default value is "scroll".
     */
    layout?: Layouts;

    /**
     * Controls the corners of the table with the "border-radius" CSS property. The default value is "rounded".
     */
    radius?: TableRadiuses;

    /**
     * Allows the table head to be sticky when scrolling inside the table body
     */
    stickyHeader?: {
      listLength: number;
      maxHeight: string;
    };

    /**
     * Total width (px) of left-pinned columns. Used to offset scroll overlay elements.
     */
    pinnedLeftWidth?: number;

    /**
     * Total width (px) of right-pinned columns. Used to offset scroll overlay elements.
     */
    pinnedRightWidth?: number;

    /**
     * When set, applied as min-width on the <table> element so columns don't collapse below their TanStack sizes.
     */
    tableMinWidth?: number;
  }
</script>

<script setup lang="ts">
  import { useIntersectionObserver, useResizeObserver, useScroll } from '@vueuse/core';
  import { computed, inject, nextTick, provide, ref, useCssModule, useTemplateRef, watch, watchEffect } from 'vue';

  import Button from '../Button/Button.vue';
  import { DATA_VIEW_INJECTION } from '../DataView/DataView.vue';
  import EmptyState from '../EmptyState/EmptyState.vue';
  import Icon from '../Icon/Icon.vue';
  import Loading from '../Loading/Loading.vue';
  import TableCell from '../TableCell/TableCell.vue';
  import TableRow from '../TableRow/TableRow.vue';
  import { TABLE_INJECTION } from './Table.keys';

  const SCROLL_DELTA_PX = 200;

  const props = withDefaults(defineProps<TableProps>(), {
    density: undefined,
    emptyStateText: '',
    hasActions: false,
    hasCustomExpandToggle: false,
    isEmpty: false,
    isSchemaDriven: false,
    isLoading: false,
    isExpandable: false,
    isSelectable: false,
    layout: 'scroll',
    radius: 'rounded',
    stickyHeader: undefined,
    pinnedLeftWidth: 0,
    pinnedRightWidth: 0,
    tableMinWidth: undefined,
  });

  const classes = useCssModule();

  const {
    density: dataViewDensity,
    variant: dataViewVariant,
    isEmpty: isDataViewEmpty,
    isLoading: isDataViewLoading,
    isSelectable: isDataViewSelectable,
  } = inject(DATA_VIEW_INJECTION.key, DATA_VIEW_INJECTION.defaults);

  const { variant: moduleVariant } = inject(MODULE_INJECTION.key, MODULE_INJECTION.defaults);

  const computedRadius = computed<TableRadiuses>(() => {
    // Will work for tables when rendered inside of a Module with OR without a DataView.
    if (moduleVariant?.value === 'table') {
      return 'rounded-bottom';
    }

    if (dataViewVariant.value === 'table') {
      return 'rounded-bottom';
    }

    return props.radius;
  });

  const computedLayout = computed<Layouts>(() => {
    if (props.stickyHeader) {
      return 'scroll';
    }

    return props.layout;
  });

  const isShadowEnabled = computed<boolean>(() => {
    return computedLayout.value === 'scroll' && dataViewVariant.value === 'table';
  });

  const isStickyHeaderEnabled = computed<boolean>(() => {
    return !!(
      (props.stickyHeader?.maxHeight && props.stickyHeader?.listLength > 3) // table can't scroll without a max height; sticky headers only needed for a scrollable table
      // scrollable table and sticky headers not needed when list is small
    );
  });

  const rootStyle = computed(() => ({
    maxHeight: isStickyHeaderEnabled.value ? props.stickyHeader?.maxHeight : '',
  }));

  const isTableLoading = computed(() => Boolean(props.isLoading || isDataViewLoading?.value));

  const scrollContainerRef = useTemplateRef<HTMLElement>('scrollContainerRef');
  const tableRef = useTemplateRef<HTMLTableElement>('tableRef');
  const hasHorizontalOverflow = ref(false);
  const scrollbarWidth = ref(0);
  const horizontalScrollbarHeight = ref(0);

  const { arrivedState, measure } = useScroll(scrollContainerRef, { behavior: 'smooth' });

  function updateOverflow() {
    const el = scrollContainerRef.value;
    if (!el || computedLayout.value !== 'scroll') return;
    hasHorizontalOverflow.value = el.scrollWidth > el.clientWidth;
    scrollbarWidth.value = el.offsetWidth - el.clientWidth;
    horizontalScrollbarHeight.value = el.offsetHeight - el.clientHeight;
    measure();
  }

  useResizeObserver(scrollContainerRef, updateOverflow);
  useResizeObserver(tableRef, updateOverflow);

  useIntersectionObserver(
    scrollContainerRef,
    (entries) => {
      if (entries[0]?.isIntersecting) updateOverflow();
    },
    { threshold: 0.01 },
  );

  watch(
    () => [props.pinnedLeftWidth, props.pinnedRightWidth, props.tableMinWidth],
    () => {
      void nextTick(() => updateOverflow());
    },
  );

  const canScrollLeft = computed(() => !arrivedState.left);
  const canScrollRight = computed(() => !arrivedState.right);
  const showScrollButtons = computed(() => computedLayout.value === 'scroll' && hasHorizontalOverflow.value);

  function scrollBy(delta: number) {
    scrollContainerRef.value?.scrollBy({ left: delta, behavior: 'smooth' });
  }

  const hasPinnedColumns = computed(() => (props.pinnedLeftWidth ?? 0) > 0 || (props.pinnedRightWidth ?? 0) > 0);

  const tableStyle = computed(() => {
    const style: Record<string, string> = {};
    if (props.tableMinWidth != null) style.minWidth = `${props.tableMinWidth}px`;
    return Object.keys(style).length ? style : undefined;
  });

  provide(TABLE_INJECTION.key, {
    density: computed(() => props.density || dataViewDensity.value || SpacingDensity.Comfortable),
    hasCustomExpandToggle: computed(() => props.hasCustomExpandToggle),
    hasActions: computed(() => props.hasActions),
    isSchemaDriven: computed(() => props.isSchemaDriven),
    isExpandable: computed(() => props.isExpandable),
    isSelectable: computed(
      () =>
        props.isSelectable &&
        !props.isLoading &&
        !isDataViewLoading?.value &&
        !props.isEmpty &&
        !isDataViewEmpty?.value,
    ),
    isLoading: isTableLoading,
    layout: computedLayout,
  });

  watchEffect(() => {
    // Table can be both casted within a DataView or standalone. useSelection is still possible to be used on both cases,
    // making it important to have Table to control selection props.
    // To avoid breaking changes and developer experience, a DataView injection is passed down and updated whenever it exists,
    // and move the information up into DataView, that sometimes aren't used.
    if (isDataViewSelectable) {
      isDataViewSelectable.value = props.isSelectable;
    }
  });
</script>

<template>
  <div class="stash-table-wrapper relative">
    <div
      ref="scrollContainerRef"
      class="stash-table relative"
      :class="[
        { rounded: computedRadius === 'rounded' },
        { 'rounded-b': computedRadius === 'rounded-bottom' },
        { 'border-t border-ice-200': dataViewVariant === 'table' },
        { 'overflow-auto scroll-smooth': computedLayout === 'scroll' },
        { shadow: isShadowEnabled },
        {
          'overflow-visible lg:overflow-auto lg:shadow': computedLayout === 'stack',
        },
      ]"
      data-test="stash-table"
      :aria-busy="isTableLoading"
      :style="rootStyle"
    >
      <table
        ref="tableRef"
        class="relative w-full"
        :class="{ 'border-separate border-spacing-0': isStickyHeaderEnabled || hasPinnedColumns }"
        :style="tableStyle"
      >
        <thead class="border-b border-ice-200" :class="{ 'hidden lg:table-header-group': computedLayout === 'stack' }">
          <!-- @slot head -->
          <slot name="head"> </slot>
        </thead>
        <tbody>
          <!--
            col-span-12 makes the td span all columns only when layout === 'stack' and the viewport is below lg (when table elements are using display: grid ).
            The colspan attr does the same in all other cases when the table elements are using their native table styles.
            relative prevents the loading/empty state from overflowing the table whenever the table has actions and the table cell applies position absolute.
            "max-width: '100vw'" prevents the loading/empty state from overflowing the table whenever the table is in the scroll layout.
            These are necessary in order to properly horizontally center Loading, and EmptyState
          -->
          <TableRow v-if="isTableLoading">
            <TableCell colspan="100%" :class="{ 'relative! col-span-12': computedLayout === 'stack' }">
              <Loading :style="[computedLayout === 'scroll' ? { 'max-width': '100vw' } : {}]" />
            </TableCell>
          </TableRow>
          <!-- @slot empty -->
          <template v-else-if="props.isEmpty || isDataViewEmpty">
            <TableRow>
              <TableCell colspan="100%" :class="{ 'relative! col-span-12': computedLayout === 'stack' }">
                <slot name="empty">
                  <EmptyState
                    class="w-full bg-white"
                    :text="props.emptyStateText"
                    :style="[computedLayout === 'scroll' ? { 'max-width': '100vw' } : {}]"
                  />
                </slot>
              </TableCell>
            </TableRow>
          </template>
          <!-- @slot body -->
          <slot v-else name="body"></slot>
        </tbody>
      </table>
    </div>
    <div
      v-if="showScrollButtons"
      :class="classes['stash-table-scroll-overlay']"
      class="pointer-events-none absolute inset-0 flex items-center justify-between z-2"
    >
      <template v-if="canScrollLeft">
        <div
          :class="classes['stash-table-scroll-shadow-left']"
          class="stash-table-scroll-shadow border-l border-ice-100 absolute top-0 w-4"
          :style="{ left: `${props.pinnedLeftWidth}px`, bottom: `${horizontalScrollbarHeight}px` }"
          data-test="stash-table|scroll-shadow-left"
        />
        <Button
          class="pointer-events-auto absolute top-[7px] border border-ice-100 bg-white rounded-full shadow size-6 p-0 min-w-auto"
          :style="{ left: `${(props.pinnedLeftWidth ?? 0) + 12}px` }"
          secondary
          icon
          :aria-label="'Scroll table left'"
          data-test="stash-table|scroll-left"
          @click="scrollBy(-SCROLL_DELTA_PX)"
        >
          <Icon name="chevron-left" />
        </Button>
      </template>
      <template v-if="canScrollRight">
        <div
          :class="classes['stash-table-scroll-shadow-right']"
          class="stash-table-scroll-shadow border-r border-ice-100 absolute top-0 w-4"
          :style="{
            right: `${scrollbarWidth + (props.pinnedRightWidth ?? 0)}px`,
            bottom: `${horizontalScrollbarHeight}px`,
          }"
          data-test="stash-table|scroll-shadow-right"
        />

        <Button
          class="pointer-events-auto absolute top-[7px] border border-ice-100 bg-white rounded-full shadow size-6 p-0 min-w-auto"
          :style="{ right: `${scrollbarWidth + (props.pinnedRightWidth ?? 0) + 12}px` }"
          secondary
          icon
          :aria-label="'Scroll table right'"
          data-test="stash-table|scroll-right"
          @click="scrollBy(SCROLL_DELTA_PX)"
        >
          <Icon name="chevron-right" class="text-ice-900" />
        </Button>
      </template>
    </div>
  </div>
</template>

<style module>
  .stash-table-scroll-shadow-left {
    background: linear-gradient(90deg, rgb(38 38 38 / 10%) 0%, rgb(38 38 38 / 0%) 32.65%);
  }

  .stash-table-scroll-shadow-right {
    background: linear-gradient(270deg, rgb(38 38 38 / 10%) 0%, rgb(38 38 38 / 0%) 32.65%);
  }

  @media print {
    .stash-table-scroll-overlay {
      display: none !important;
    }
  }
</style>
